/**
 * PHARMAKON — the print layer configuration (refonte WP-19 · F4, design/ux/01_foundations.md §3.16).
 *
 * A printed document is a physical artefact: its geometry is in millimetres and its type in points,
 * never in the screen's px / rem. This module is the ONLY place those physical constants live — the
 * page rules the print frame injects (`lib/print-frame.js`), the stylesheet (`styles/print.css`,
 * through the custom properties of `labelVars` / `pageVars`) and the components
 * (`components/print/`) read them; no component carries a paper size, a margin, a bar height or a
 * printed French label as a literal. Ported from NoveraMED's print engine
 * (`apps/web-medecin/src/config/print.config.js`), pharmacy formats of ux/01 §3.16.
 *
 * The label formats themselves (keys, sizes, the « Format d'étiquette par défaut » of Paramètres ›
 * Achats et étiquettes) come from the shared constants the server validates against
 * (`@pharmakon/shared/constants/parametres-pharmacie.js`, D-16) — never re-typed here.
 */
import {
  ETIQUETTE_COPIES as ETIQUETTE_COPIES_PARTAGEES,
  ETIQUETTE_FORMATS,
  PARAMETRES_PHARMACIE_DEFAUTS,
} from "@pharmakon/shared/constants/parametres-pharmacie.js";
import { CODE128_ZONE_CALME } from "@pharmakon/shared/barcode/code128.js";

/** CSS reference pixels per millimetre (96 dpi CSS reference). */
export const PX_PER_MM = 96 / 25.4;

/** mm → CSS px, rounded (screen preview only). */
export const mmToPx = (mm) => Math.round(mm * PX_PER_MM);

/** Thermal label printers of D-16 (Zebra / TSC / Xprinter class) print at 203 dpi. */
export const THERMAL_DPI = 203;

/** One printer dot in mm at THERMAL_DPI. */
export const THERMAL_DOT_MM = 25.4 / THERMAL_DPI;

/** Kinds of printed page (the `kind` of PRINT_PAGES entries). */
export const PRINT_KINDS = Object.freeze({ A4: "a4", ETIQUETTE: "etiquette" });

/**
 * A4 document type scale and rules (ux/01 §3.16 « A4 documents »): body 10 pt, table cells 9.5 pt
 * minimum, title 14 pt / 700, 0.5 pt rules; the footer line is the smallest text on the page.
 */
export const A4_TYPE = Object.freeze({
  body_pt: 10,
  cell_pt: 9.5,
  title_pt: 14,
  heading_pt: 11,
  small_pt: 8.5,
  footer_pt: 8,
  rule_pt: 0.5,
});

/** ux/01 §3.16: `@page { size: A4 portrait; margin: 12mm 12mm 14mm }`; journals in landscape. */
const A4_MARGIN_MM = Object.freeze({ top: 12, right: 12, bottom: 14, left: 12 });

/**
 * Per-format label layout (ux/01 §3.16 « Thermal labels » table, the single source the label
 * dialog of 02 §6.7 points to). Sizes in pt (type) and mm (geometry).
 */
export const ETIQUETTE_LAYOUTS = Object.freeze({
  "40x25": Object.freeze({
    padding_mm: 1.5,
    produit_pt: 7.5,
    prix_pt: 10,
    /** A price longer than this (« 1 250,00 DA ») prints at `prix_long_pt` so it never meets the code. */
    prix_car_max: 9,
    prix_long_pt: 8,
    pharmacie_pt: 6,
    code_pt: 6.5,
    barre_hauteur_mm: 9,
    module_mm: 0.25,
    /** 40 × 25: name, dosage, forme and conditionnement on one block clamped to 2 lines. */
    lignes_produit: 2,
    detail_separe: false,
  }),
  "50x30": Object.freeze({
    padding_mm: 2,
    produit_pt: 9,
    prix_pt: 12,
    prix_car_max: 10,
    prix_long_pt: 10,
    pharmacie_pt: 7,
    code_pt: 7.5,
    barre_hauteur_mm: 11,
    module_mm: 0.33,
    /** 50 × 30: « DOLIPRANE 1000 mg » then « Comprimé · B/8 » (02 §6.7 layout). */
    lignes_produit: 2,
    detail_separe: true,
  }),
});

/** ux/01 §3.16: the printed-label text floor is 6 pt (the 13 px screen floor of P-01 does not apply). */
export const ETIQUETTE_TEXTE_MIN_PT = 6;

/** ux/01 §3.16: a module is at least 2 printer dots at 203 dpi (0.25 mm). */
export const ETIQUETTE_MODULE_MIN_MM = 0.25;

/** Quiet zone each side of the bars, in modules (≥ 10, ISO/IEC 15417 — the encoder's own floor). */
export const ETIQUETTE_ZONE_CALME_MODULES = CODE128_ZONE_CALME;

/** 02 §6.7: the dialog previews one label at 2:1. */
export const ETIQUETTE_APERCU_ECHELLE = 2;

/** 02 §6.7 « nombre d'étiquettes » QtyInput bounds (M-S3-21) — the server CONFIG twin (WP-26). */
export const ETIQUETTE_COPIES = ETIQUETTE_COPIES_PARTAGEES;

/** Default label format when the pharmacy has none (the shared settings default, D-16). */
export const ETIQUETTE_FORMAT_DEFAUT = PARAMETRES_PHARMACIE_DEFAUTS.etiquette_format;

const pageEtiquette = (f) =>
  Object.freeze({
    cle: `etiquette-${f.cle}`,
    kind: PRINT_KINDS.ETIQUETTE,
    format: f.cle,
    libelle: `Étiquette ${f.libelle}`,
    size: `${f.largeur_mm}mm ${f.hauteur_mm}mm`,
    width_mm: f.largeur_mm,
    height_mm: f.hauteur_mm,
    margin_mm: Object.freeze({ top: 0, right: 0, bottom: 0, left: 0 }),
    footer: false,
  });

/**
 * Every printed page the layer renders, keyed by the `data-print-page` value a document declares
 * on its `[data-print-doc]` root. The print frame builds the `@page` rule from this entry.
 */
export const PRINT_PAGES = Object.freeze({
  "a4-portrait": Object.freeze({
    cle: "a4-portrait",
    kind: PRINT_KINDS.A4,
    libelle: "A4 portrait",
    size: "A4 portrait",
    width_mm: 210,
    height_mm: 297,
    margin_mm: A4_MARGIN_MM,
    footer: true,
  }),
  "a4-landscape": Object.freeze({
    cle: "a4-landscape",
    kind: PRINT_KINDS.A4,
    libelle: "A4 paysage",
    size: "A4 landscape",
    width_mm: 297,
    height_mm: 210,
    margin_mm: A4_MARGIN_MM,
    footer: true,
  }),
  ...Object.fromEntries(
    Object.values(ETIQUETTE_FORMATS).map((f) => [`etiquette-${f.cle}`, pageEtiquette(f)]),
  ),
});

export const PRINT_PAGE_DEFAUT = "a4-portrait";

/** Resolve a page key; unknown keys degrade to A4 portrait (never a blank print). */
export function printPage(cle) {
  return PRINT_PAGES[String(cle || "")] ?? PRINT_PAGES[PRINT_PAGE_DEFAUT];
}

/** The page key of an A4 document. */
export const pageA4 = (orientation) => (orientation === "landscape" ? "a4-landscape" : "a4-portrait");

/** Resolve a label format key (the Paramètres value); unknown keys degrade to the default. */
export function etiquetteFormat(cle) {
  const k = ETIQUETTE_FORMATS[cle] ? cle : ETIQUETTE_FORMAT_DEFAUT;
  return Object.freeze({ ...ETIQUETTE_FORMATS[k], ...ETIQUETTE_LAYOUTS[k], page: `etiquette-${k}` });
}

/** Label formats for a Select (`value` = the Paramètres key). */
export const ETIQUETTE_FORMAT_OPTIONS = Object.freeze(
  Object.values(ETIQUETTE_FORMATS).map((f) => Object.freeze({ value: f.cle, label: f.libelle })),
);

/** CSS custom properties of an A4 page (the screen preview width; print uses @page). */
export function pageVars(cle) {
  const p = printPage(cle);
  const m = p.margin_mm;
  return {
    "--pp-page-w": `${p.width_mm}mm`,
    "--pp-page-min-h": `${p.height_mm}mm`,
    "--pp-pad": `${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm`,
  };
}

/** CSS custom properties of a label format (one source for the print and the 2:1 preview). */
export function labelVars(cle) {
  const f = etiquetteFormat(cle);
  return {
    "--pl-w": `${f.largeur_mm}mm`,
    "--pl-h": `${f.hauteur_mm}mm`,
    "--pl-pad": `${f.padding_mm}mm`,
    "--pl-fs-produit": `${f.produit_pt}pt`,
    "--pl-fs-prix": `${f.prix_pt}pt`,
    "--pl-fs-prix-long": `${f.prix_long_pt}pt`,
    "--pl-fs-pharmacie": `${f.pharmacie_pt}pt`,
    "--pl-fs-code": `${f.code_pt}pt`,
    "--pl-barre-h": `${f.barre_hauteur_mm}mm`,
    "--pl-lignes": String(f.lignes_produit),
  };
}

/** Print-frame timings (ms): how long the frame waits for the logo / fonts before printing anyway. */
export const PRINT_FRAME = Object.freeze({
  ASSET_TIMEOUT_MS: 4000,
  /** The frame is removed after `afterprint`, or after this delay when the event never comes. */
  CLEANUP_FALLBACK_MS: 60000,
  /** « Préparation de l'impression… » is announced only when preparing takes longer than this. */
  ANNOUNCE_AFTER_MS: 300,
});

/** The printed French vocabulary and the print messages (02 §6.7 M-S3-22 / M-S3-23). */
export const PRINT_LABELS = Object.freeze({
  page: "Page",
  imprimeLe: "Imprimé le",
  a: "à",
  par: "par",
  statut: "Statut",
  numero: "N°",
  date: "Date",
  totaux: "Totaux",
  suite: "suite",
  telephone: "Tél.",
  agrement: "Agrément",
  aucuneLigne: "Aucune ligne.",
  apercu: "Aperçu",
  apercuEtiquette: "Aperçu de l'étiquette (échelle 2:1)",
  documentSansTitre: "Document",
});

export const PRINT_MESSAGES = Object.freeze({
  preparation: "Préparation de l'impression…",
  /** M-S3-23 (labels); the A4 wording names the printer generically. */
  echecEtiquettes:
    "L'impression n'a pas pu être préparée. Vérifiez que l'imprimante d'étiquettes est sélectionnée, puis réessayez.",
  echecDocument: "L'impression n'a pas pu être préparée. Vérifiez l'imprimante sélectionnée, puis réessayez.",
  /** M-S3-22 */
  codeAbsent: "Code lot non encore attribué — impression impossible pour ce lot.",
  /** M-S3-21 */
  copiesInvalides: `Saisissez un nombre d'étiquettes entre ${ETIQUETTE_COPIES.MIN} et ${ETIQUETTE_COPIES.MAX}.`,
  /** M-S3-20 */
  aucuneEtiquette: "Cochez au moins un lot à imprimer.",
  enCours: "Une impression est déjà en préparation.",
  codeTropLong: "Ce code ne tient pas sur ce format d'étiquette — choisissez un format plus large.",
});

/** ux/01 §3.16: the short help on the browser print settings for a thermal roll. */
export const ETIQUETTE_AIDE_IMPRESSION = Object.freeze({
  titre: "Réglages d'impression des étiquettes",
  etapes: Object.freeze([
    "Destination : choisissez l'imprimante d'étiquettes (rouleau thermique).",
    "Marges : « Aucune ».",
    "Échelle : 100 % (ne pas « Ajuster à la page »).",
    "En-têtes et pieds de page : décochés.",
  ]),
  note: "Le format du rouleau (40 × 25 mm ou 50 × 30 mm) doit être celui choisi dans la fenêtre.",
});

export default PRINT_PAGES;
